import { api } from '@xtnd/web/shared/api';
import type { ClioWebhookModel } from '@xtnd/web/shared/types/tenantSettingTypes';
import { CLIO_WEBHOOK_MODELS } from '../constants/webhookModels';

export interface WebhookSubscription {
  modelName: string;
  enabled: boolean;
  events: string[];
  clioWebhookId?: string;
  status: string;
  autoProcessingEnabled?: boolean;
  webhookUrl?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface WebhookLifecycleResult {
  success: boolean;
  webhookId?: string;
  autoProcessingEnabled?: boolean;
  message: string;
  error?: string;
}

export interface CreateWebhookRequest {
  modelName: string;
  events: string[];
}

export interface ClioInspectionResult {
  success: boolean;
  webhooks: any[];
  message: string;
}

export interface RecreateAllResult {
  success: boolean;
  results: Array<{ modelName: string; success: boolean; webhookId?: string; error?: string }>;
}

class ClioWebhooksApi {
  private readonly baseSource = 'tenants/current/webhooks/clio';
  private readonly lifecycleBase = `${this.baseSource}/lifecycle`;

  /**
   * Create webhook with auto-enable
   */
  async createWithAutoEnable(request: CreateWebhookRequest): Promise<WebhookLifecycleResult> {
    return api.post(`${this.lifecycleBase}/create`, request);
  }

  /**
   * Delete webhook completely (from Clio and local config)
   */
  async deleteComplete(modelName: string): Promise<WebhookLifecycleResult> {
    return api.delete(`${this.lifecycleBase}/delete/${modelName}`);
  }

  /**
   * Recreate webhook from existing config
   */
  async recreateFromConfig(modelName: string, events?: string[]): Promise<WebhookLifecycleResult> {
    return api.post(`${this.lifecycleBase}/recreate/${modelName}`, { events });
  }

  /**
   * Recreate all webhooks
   */
  async recreateAll(): Promise<RecreateAllResult> {
    return api.post(`${this.lifecycleBase}/recreate-all`, {});
  }

  /**
   * Get current webhook subscriptions
   */
  async getWebhooks(): Promise<WebhookSubscription[]> {
    const response = await api.get<{
      subscriptions?: WebhookSubscription[];
      webhooks?: WebhookSubscription[];
    }>(this.baseSource);
    return response.subscriptions || response.webhooks || [];
  }

  /**
   * Inspect webhooks directly from Clio
   */
  async getClioWebhookInspection(): Promise<ClioInspectionResult> {
    return api.get(`${this.lifecycleBase}/clio-inspection`);
  }

  /**
   * Delete webhook from Clio only
   */
  async deleteFromClio(webhookId: string): Promise<{ success: boolean; message: string }> {
    return api.delete(`${this.lifecycleBase}/delete-clio/${webhookId}`);
  }

  /**
   * Get available webhook models
   */
  getWebhookModels(): ClioWebhookModel[] {
    return CLIO_WEBHOOK_MODELS;
  }
}

export const clioWebhooksApi = new ClioWebhooksApi();

// Re-export constants for convenience
export { CLIO_WEBHOOK_MODELS } from '../constants/webhookModels';

